import React from "react";
import { Panel } from "@ozpanel/ui";

// PhasedSteps — a bootstrap/join job's steps, grouped by phase, each expandable to what it reported.
//
// Shared by the install screen (this node's own install) and the add-node screen (the master's join
// steps and then the joiner's own bootstrap): the same job shape, recorded by the same engine, so one
// rendering. A failed or running step opens by itself — that is the one an operator came to read.

export type JobStepView = {
  name: string;
  phase: number;
  label: string;
  status: string;
  detail?: string;
  started_at?: string;
  finished_at?: string;
};

/** The install plan's phases (cluster/steps.go phaseLabels names the same numbers). */
export const INSTALL_PHASES: Record<number, string> = {
  0: "Provider setup",
  1: "Base system",
  2: "Services",
  3: "Panel",
  4: "Cluster membership",
  5: "Verification",
};

function duration(s: JobStepView): string {
  if (!s.started_at) return "";
  const end = s.finished_at ? new Date(s.finished_at).getTime() : Date.now();
  const sec = Math.max(0, Math.round((end - new Date(s.started_at).getTime()) / 1000));
  return sec >= 60 ? `${Math.floor(sec / 60)}m ${sec % 60}s` : `${sec}s`;
}

export function StatusMark({ status }: { status: string }) {
  const m: Record<string, [string, string]> = {
    done: ["✓", "text-ok"],
    running: ["●", "text-accent animate-pulse"],
    failed: ["✕", "text-crit"],
    skipped: ["–", "text-faint"],
    pending: ["○", "text-faint"],
  };
  const [ch, cls] = m[status] ?? ["?", "text-faint"];
  return <span className={`inline-block w-4 text-center ${cls}`}>{ch}</span>;
}

export function PhasedSteps({ steps, phases = INSTALL_PHASES }: { steps: JobStepView[]; phases?: Record<number, string> }) {
  const [open, setOpen] = React.useState<Record<string, boolean>>({});
  const nums = [...new Set(steps.map((s) => s.phase))].sort((a, b) => a - b);
  return (
    <div className="space-y-3">
      {nums.map((ph) => (
        <Panel key={ph} className="p-3">
          <h2 className="mb-1.5 text-[12px] font-medium tracking-wide text-muted uppercase">
            Phase {ph} · {phases[ph] ?? ""}
          </h2>
          <ul className="space-y-0.5">
            {steps
              .filter((s) => s.phase === ph)
              .map((s) => {
                const expanded = open[s.name] ?? (s.status === "failed" || s.status === "running");
                return (
                  <li key={s.name} className="text-[13px]">
                    <button
                      type="button"
                      className="flex w-full items-baseline gap-2 text-left hover:bg-bg2"
                      onClick={() => setOpen((o) => ({ ...o, [s.name]: !expanded }))}
                      disabled={!s.detail}
                    >
                      <StatusMark status={s.status} />
                      <span className="flex-1">
                        {s.label || s.name} <span className="font-mono text-[11.5px] text-faint">{s.name}</span>
                      </span>
                      <span className="font-mono text-[11.5px] text-faint">{duration(s)}</span>
                    </button>
                    {expanded && s.detail && (
                      <pre className="mt-1 mb-1.5 ml-6 max-h-72 overflow-auto rounded bg-bg p-2 font-mono text-[11.5px] whitespace-pre-wrap text-muted">
                        {s.detail}
                      </pre>
                    )}
                  </li>
                );
              })}
          </ul>
        </Panel>
      ))}
    </div>
  );
}
